import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import IconSvg from 'src/assets/icons/IconSvg';
import Button from 'src/components/button/Button';
import { useRaffles } from 'src/hooks/useRaffles';
import { useRaffleParticipants } from 'src/hooks/useRaffleParticipants';
import { useAuth } from 'src/hooks/useAuth';
import { appConfig } from 'src/config/appConfig';
import {
  getRaffleDisplayName,
  getRaffleImage,
  satsToBtc,
  formatTicketsSold,
} from 'src/utils/raffleHelpers';
import type { Raffle, RaffleWinner } from 'src/types/raffle';

const truncateId = (str: string) =>
  !str || str.length <= 12 ? str : `${str.slice(0, 5)}...${str.slice(-3)}`;

const WinnerTestPage = () => {
  const { accessToken, userId } = useAuth();
  const { raffles, isLoading: rafflesLoading, fetchRaffles } = useRaffles();
  const {
    participants,
    participantsMeta,
    isLoadingParticipants,
    fetchParticipants,
  } = useRaffleParticipants();

  const [selectedRaffle, setSelectedRaffle] = useState<Raffle | null>(null);
  const [winners, setWinners] = useState<RaffleWinner[]>([]);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectError, setSelectError] = useState<string | null>(null);
  const [claimStatuses, setClaimStatuses] = useState<Record<string, 'PENDING' | 'CLAIMED' | 'EXPIRED'>>({});

  const headers = {
    'Content-Type': 'application/json',
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    'X-Api-Key': process.env.REACT_APP_API_KEY as string,
  };

  // Fetch LIVE raffles on mount
  useEffect(() => {
    fetchRaffles({ status: 'LIVE', limit: 50, sortBy: 'endDate', sortOrder: 'asc' });
  }, [fetchRaffles]);

  // When a raffle is selected, fetch its participants and existing winners
  useEffect(() => {
    if (!selectedRaffle) return;
    fetchParticipants(selectedRaffle.id, 1, 100);
    fetchExistingWinners(selectedRaffle.id);
  }, [selectedRaffle?.id]);

  const fetchExistingWinners = useCallback(
    async (raffleId: string) => {
      try {
        const res = await axios.get(
          `${appConfig.serviceUrl}/raffles/${raffleId}/winners`,
          { headers },
        );
        setWinners(res.data || []);
      } catch {
        setWinners([]);
      }
    },
    [headers],
  );

  const handleSelectRaffle = (raffle: Raffle) => {
    setSelectedRaffle(raffle);
    setWinners([]);
    setSelectError(null);
    setClaimStatuses({});
  };

  const handleSelectWinners = async () => {
    if (!selectedRaffle || !userId) return;
    setIsSelecting(true);
    setSelectError(null);

    try {
      const res = await axios.post(
        `${appConfig.serviceUrl}/raffles/${selectedRaffle.id}/select-winners`,
        { userId },
        { headers },
      );
      setWinners(res.data || []);
      // Refresh raffle list to update statuses
      fetchRaffles({ status: 'LIVE', limit: 50, sortBy: 'endDate', sortOrder: 'asc' });
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to select winners';
      setSelectError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setIsSelecting(false);
    }
  };

  const handleClaimPrize = async (raffleId: string) => {
    if (!userId) return;
    try {
      await axios.patch(
        `${appConfig.serviceUrl}/raffles/${raffleId}/claim`,
        { userId },
        { headers },
      );
      // Refresh winners
      fetchExistingWinners(raffleId);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to claim';
      setSelectError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    }
  };

  const handleClaimStatusToggle = (winnerId: string, status: 'PENDING' | 'CLAIMED' | 'EXPIRED') => {
    setClaimStatuses(prev => ({ ...prev, [winnerId]: status }));
  };

  const isEndDatePassed = selectedRaffle
    ? new Date() >= new Date(selectedRaffle.endDate)
    : false;
  const hasExistingWinners = winners.length > 0;
  const canSelectWinners =
    selectedRaffle &&
    userId &&
    selectedRaffle.creatorId === userId &&
    selectedRaffle.status === 'LIVE' &&
    isEndDatePassed &&
    !hasExistingWinners;

  return (
    <div className="max-w-[1200px] mx-auto">
      <h3 className="mb-2 text-brand-magnolia">Winner Selection Test</h3>
      <p className="mb-6 text-grayscale-400 text-sm">
        Select a live raffle, view its participants, and draw winners using the real API.
        Only the raffle creator can select winners after the end date has passed.
      </p>

      {!userId && (
        <div className="mb-6 p-4 rounded-lg border border-system-error bg-grayscale-800">
          <div className="text-system-error text-sm">
            Connect your wallet to use this page. You need to be the raffle creator to select winners.
          </div>
        </div>
      )}

      {/* Raffle List */}
      <div className="mb-6">
        <div className="mb-3 text-sm text-grayscale-300 font-medium">
          Live Raffles ({raffles.length})
        </div>

        {rafflesLoading && (
          <div className="text-grayscale-400 text-center py-6">Loading raffles...</div>
        )}

        {!rafflesLoading && raffles.length === 0 && (
          <div className="text-grayscale-400 text-center py-6">
            No live raffles found
          </div>
        )}

        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
          {raffles.map((raffle) => {
            const endPassed = new Date() >= new Date(raffle.endDate);
            const isCreator = userId === raffle.creatorId;

            return (
              <button
                key={raffle.id}
                className={`rounded-lg border p-3 text-left transition-all ${
                  selectedRaffle?.id === raffle.id
                    ? 'border-brand-tangerine bg-grayscale-800'
                    : 'border-transparent bg-grayscale-800 hover:bg-grayscale-700'
                }`}
                onClick={() => handleSelectRaffle(raffle)}
              >
                <img
                  src={getRaffleImage(raffle)}
                  alt={getRaffleDisplayName(raffle)}
                  className="w-full h-[100px] object-cover rounded-lg mb-2"
                />
                <div className="text-sm text-brand-magnolia font-medium truncate">
                  {getRaffleDisplayName(raffle)}
                </div>
                <div className="text-xs text-grayscale-400 mt-0.5">
                  {formatTicketsSold(raffle)}
                </div>
                <div className="flex gap-2 mt-1.5">
                  {endPassed && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-system-error/20 text-system-error">
                      Ended
                    </span>
                  )}
                  {isCreator && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-brand-tangerine/20 text-brand-tangerine">
                      Your raffle
                    </span>
                  )}
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-grayscale-700 text-grayscale-300">
                    {raffle.numberOfWinners} winner{raffle.numberOfWinners > 1 ? 's' : ''}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected Raffle Detail */}
      {selectedRaffle && (
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_420px] gap-6">
          {/* Left: Raffle Info + Participants */}
          <div className="space-y-4">
            {/* Raffle Stats */}
            <div className="p-6 rounded-lg bg-grayscale-800">
              <div className="flex gap-4 items-center mb-4">
                <img
                  src={getRaffleImage(selectedRaffle)}
                  alt={getRaffleDisplayName(selectedRaffle)}
                  className="w-16 h-16 rounded-lg object-cover"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-lg font-semibold text-brand-magnolia truncate">
                    {getRaffleDisplayName(selectedRaffle)}
                  </div>
                  <div className="text-sm text-grayscale-400">
                    {selectedRaffle.ordinalCollectionName || selectedRaffle.runesTokenName || 'Unknown'}
                  </div>
                </div>
                <div
                  className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-medium ${
                    selectedRaffle.status === 'LIVE'
                      ? 'bg-system-success/20 text-system-success'
                      : selectedRaffle.status === 'ENDED'
                        ? 'bg-brand-tangerine/20 text-brand-tangerine'
                        : 'bg-grayscale-700 text-grayscale-300'
                  }`}
                >
                  {selectedRaffle.status}
                </div>
              </div>

              <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
                <div>
                  <div className="text-xs text-grayscale-400">Ticket Price</div>
                  <div className="flex gap-1 items-center">
                    <IconSvg icon="bitcoinS" />
                    <span className="text-brand-magnolia font-medium">
                      {satsToBtc(selectedRaffle.ticketPriceSats).toFixed(4)}
                    </span>
                  </div>
                </div>
                <div>
                  <div className="text-xs text-grayscale-400">Tickets Sold</div>
                  <div className="text-brand-magnolia font-medium">
                    {formatTicketsSold(selectedRaffle)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-grayscale-400">Winners</div>
                  <div className="text-brand-magnolia font-medium">
                    {selectedRaffle.numberOfWinners}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-grayscale-400">End Date</div>
                  <div className="text-brand-magnolia font-medium text-sm">
                    {new Date(selectedRaffle.endDate).toLocaleString()}
                  </div>
                  {isEndDatePassed && (
                    <div className="text-[10px] text-system-error">Past end date</div>
                  )}
                </div>
              </div>
            </div>

            {/* Select Winners Button */}
            <div className="space-y-2">
              <div className="flex gap-3">
                <Button
                  variant="primary"
                  size="md"
                  className="flex-1"
                  onClick={handleSelectWinners}
                  disabled={!canSelectWinners || isSelecting}
                >
                  {isSelecting ? 'Selecting...' : 'Select Winners'}
                </Button>
                {hasExistingWinners && (
                  <Button
                    variant="secondary"
                    size="md"
                    onClick={() => fetchExistingWinners(selectedRaffle.id)}
                  >
                    Refresh
                  </Button>
                )}
              </div>

              {/* Status messages */}
              {!canSelectWinners && selectedRaffle && userId && (
                <div className="text-xs text-grayscale-400 space-y-0.5">
                  {selectedRaffle.creatorId !== userId && (
                    <div className="text-system-error">
                      You are not the creator of this raffle
                    </div>
                  )}
                  {!isEndDatePassed && (
                    <div className="text-system-error">
                      Raffle end date has not passed yet
                    </div>
                  )}
                  {hasExistingWinners && (
                    <div className="text-brand-tangerine">
                      Winners have already been selected
                    </div>
                  )}
                </div>
              )}

              {selectError && (
                <div className="p-3 rounded-lg bg-system-error/10 border border-system-error text-sm text-system-error">
                  {selectError}
                </div>
              )}
            </div>

            {/* Participants Table */}
            <div className="p-4 rounded-lg bg-grayscale-800">
              <div className="text-sm font-medium text-grayscale-300 mb-3">
                Participants ({participantsMeta?.total ?? participants.length})
              </div>
              <div className="grid grid-cols-[1fr_1fr] py-2 text-xs text-grayscale-400 border-b border-grayscale-700">
                <div>User</div>
                <div>Tickets Bought</div>
              </div>
              <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
                {isLoadingParticipants ? (
                  <div className="py-6 text-center text-grayscale-400 text-sm">
                    Loading participants...
                  </div>
                ) : participants.length === 0 ? (
                  <div className="py-6 text-center text-grayscale-400 text-sm">
                    No participants yet
                  </div>
                ) : (
                  participants.map((p, i) => {
                    const isWinner = winners.some(
                      (w) => w.winnerId === p.buyerId,
                    );
                    return (
                      <div
                        key={`${p.buyerId}-${i}`}
                        className={`grid grid-cols-[1fr_1fr] py-2.5 text-sm ${
                          isWinner
                            ? 'text-brand-tangerine font-semibold'
                            : 'text-brand-magnolia'
                        }`}
                      >
                        <div className="flex items-center gap-1">
                          {isWinner && <span>&#127942;</span>}
                          {truncateId(p.buyerId)}
                        </div>
                        <div>{p._sum.quantity}</div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* Right: Winner Card Preview */}
          <div className="space-y-4">
            <div className="text-sm font-medium text-grayscale-300">
              Winner Card Preview
            </div>

            {isSelecting && (
              <div className="p-6 rounded-lg bg-grayscale-800 text-center">
                <div className="text-4xl mb-3 animate-bounce">&#127922;</div>
                <div className="text-lg font-medium text-brand-magnolia animate-pulse">
                  Selecting winners...
                </div>
              </div>
            )}

            {!isSelecting && winners.length === 0 && (
              <div className="p-6 rounded-lg bg-grayscale-800 text-center">
                <div className="text-4xl mb-3 opacity-30">&#127942;</div>
                <div className="text-grayscale-400">No winners yet</div>
                <div className="text-xs text-grayscale-500 mt-1">
                  Select winners to see the card design
                </div>
              </div>
            )}

            {/* Winner Cards */}
            {winners.map((winner) => {
              const displayStatus =
                claimStatuses[winner.id] ?? winner.claimStatus;
              const isUserWinner = userId === winner.winnerId;

              return (
                <div key={winner.id}>
                  {/* The actual winner card design */}
                  <div className="p-6 rounded-lg bg-[#FF991A] text-brand-black">
                    <div className="flex flex-col items-center text-center space-y-3">
                      <div className="text-4xl">&#127942;</div>
                      <h3 className="text-[24px] font-bold">
                        {winners.length > 1
                          ? `Winner #${winner.winnerRank}`
                          : 'Winner!'}
                      </h3>

                      <div className="flex gap-3 items-center">
                        <div className="w-10 h-10 bg-brand-black/20 rounded-full" />
                        <div className="text-lg font-medium">
                          @{truncateId(winner.winnerId)}
                        </div>
                      </div>

                      <div className="text-sm">
                        Winning Ticket: #{winner.winningTicketNumber}
                      </div>

                      <div className="text-sm">
                        Rank: {winner.winnerRank}
                      </div>

                      {displayStatus === 'PENDING' && isUserWinner && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="mt-2"
                          onClick={() =>
                            handleClaimPrize(selectedRaffle.id)
                          }
                        >
                          Claim Prize
                        </Button>
                      )}

                      {displayStatus === 'PENDING' && !isUserWinner && (
                        <div className="mt-2 px-4 py-2 rounded-lg bg-brand-black/20 text-sm font-medium">
                          Awaiting Claim
                        </div>
                      )}

                      {displayStatus === 'CLAIMED' && (
                        <div className="mt-2 px-4 py-2 rounded-lg bg-brand-black/20 text-sm font-medium">
                          Prize Claimed
                        </div>
                      )}

                      {displayStatus === 'EXPIRED' && (
                        <div className="mt-2 px-4 py-2 rounded-lg bg-brand-black/20 text-sm font-medium opacity-60">
                          Claim Expired
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Test Controls: toggle status for design preview */}
                  <div className="mt-2">
                    <div className="text-[10px] text-grayscale-500 mb-1">
                      Preview claim status:
                    </div>
                    <div className="flex gap-2">
                      {(['PENDING', 'CLAIMED', 'EXPIRED'] as const).map(
                        (status) => (
                          <button
                            key={status}
                            className={`flex-1 px-2 py-1.5 rounded text-xs ${
                              displayStatus === status
                                ? status === 'PENDING'
                                  ? 'bg-brand-tangerine text-brand-black'
                                  : status === 'CLAIMED'
                                    ? 'bg-system-success text-brand-black'
                                    : 'bg-system-error text-brand-magnolia'
                                : 'bg-grayscale-800 text-grayscale-400'
                            }`}
                            onClick={() =>
                              handleClaimStatusToggle(winner.id, status)
                            }
                          >
                            {status}
                          </button>
                        ),
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Prize Info */}
            {winners.length > 0 && selectedRaffle && (
              <div className="p-4 rounded-lg bg-grayscale-800 space-y-3">
                <div className="text-sm font-medium text-grayscale-300">
                  Prize Details
                </div>
                <div className="flex gap-4 items-center">
                  <img
                    src={getRaffleImage(selectedRaffle)}
                    alt={getRaffleDisplayName(selectedRaffle)}
                    className="w-14 h-14 rounded-lg object-cover"
                  />
                  <div>
                    <div className="text-brand-magnolia font-medium">
                      {getRaffleDisplayName(selectedRaffle)}
                    </div>
                    <div className="text-xs text-grayscale-400">
                      {selectedRaffle.ordinalCollectionName ||
                        selectedRaffle.runesTokenName ||
                        'Unknown'}
                    </div>
                  </div>
                </div>
                <div className="text-xs text-grayscale-400">
                  Total Value:{' '}
                  <span className="text-brand-magnolia">
                    {(
                      satsToBtc(selectedRaffle.ticketPriceSats) *
                      selectedRaffle.ticketsSold
                    ).toFixed(4)}{' '}
                    BTC
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WinnerTestPage;
